import { useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Email Alerts Row — Toggle + test-send ───────────────────────────────────
export function EmailAlertsRow({ settings, saveNotif }) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const enabled = !!settings?.notifications?.emailAlerts;
  const email = settings?.email || settings?.profile?.email || "";

  const sendTest = async () => {
    setSending(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/email/test`);
      setResult({ ok: true, msg: `Sent to ${r.data?.sent_to || email}` });
    } catch (e) {
      setResult({ ok: false, msg: e.response?.data?.detail || e.message || "Send failed" });
    }
    setSending(false);
  };

  return (
    <>
      <div className="srow">
        <div>
          <div className="slbl">Email Alerts {email && <span style={{ color: "var(--muted)", fontWeight: 400, fontSize: 10 }}>· sends to {email}</span>}</div>
          <div className="ssub">Risk Guard + Trail-Stop trigger emails. Save your email in Account Profile first.</div>
        </div>
        <input
          type="checkbox"
          data-testid="setting-email-alerts"
          checked={enabled}
          onChange={e => saveNotif("emailAlerts", e.target.checked)}
        />
      </div>
      {enabled && (
        <div className="srow" style={{ borderTop: "none" }}>
          <div>
            <div className="slbl">Send test email</div>
            <div className="ssub">Delivers a sample Omega alert to confirm Resend works.</div>
            {result && (
              <div style={{ fontSize: 10, color: result.ok ? "var(--green)" : "var(--red)", marginTop: 4 }}>
                {result.ok ? "✓ " : "✗ "}{result.msg}
              </div>
            )}
          </div>
          <button
            data-testid="send-test-email-btn"
            onClick={sendTest}
            disabled={sending || !email}
            className="btn btn-blue"
            style={{ fontSize: 10, opacity: (sending || !email) ? 0.55 : 1 }}
          >{sending ? "Sending..." : "Send Test"}</button>
        </div>
      )}
      <MorningBriefingRow settings={settings} saveNotif={saveNotif} email={email} emailEnabled={enabled} />
      <EveningWrapRow settings={settings} saveNotif={saveNotif} email={email} emailEnabled={enabled} />
      <WeeklyReviewRow settings={settings} saveNotif={saveNotif} email={email} emailEnabled={enabled} />
    </>
  );
}

function WeeklyReviewRow({ settings, saveNotif, email, emailEnabled }) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const enabled = !!settings?.notifications?.weeklyReview;
  const canSend = !!email && emailEnabled;

  const sendNow = async () => {
    setSending(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/email/weekly/send-now`);
      setResult({ ok: true, msg: `Sent to ${r.data?.sent_to}` });
    } catch (e) {
      setResult({ ok: false, msg: e.response?.data?.detail || e.message || "Send failed" });
    }
    setSending(false);
  };

  return (
    <>
      <div className="srow">
        <div>
          <div className="slbl">Week in Review Email</div>
          <div className="ssub">Every Friday 4:30 PM ET: weekly P/L + best/worst positions + all trades + triggered trail stops + Omega market read. Requires Email Alerts ON.</div>
        </div>
        <input
          type="checkbox"
          data-testid="setting-weekly-review"
          checked={enabled}
          disabled={!emailEnabled}
          onChange={e => saveNotif("weeklyReview", e.target.checked)}
        />
      </div>
      {enabled && (
        <div className="srow" style={{ borderTop: "none" }}>
          <div>
            <div className="slbl">Send weekly review now</div>
            <div className="ssub">Preview the weekly review with the last 7 days of data.</div>
            {result && (
              <div style={{ fontSize: 10, color: result.ok ? "var(--green)" : "var(--red)", marginTop: 4 }}>
                {result.ok ? "✓ " : "✗ "}{result.msg}
              </div>
            )}
          </div>
          <button
            data-testid="send-weekly-now-btn"
            onClick={sendNow}
            disabled={sending || !canSend}
            className="btn btn-blue"
            style={{ fontSize: 10, opacity: (sending || !canSend) ? 0.55 : 1 }}
          >{sending ? "Sending..." : "Send Now"}</button>
        </div>
      )}
    </>
  );
}

function EveningWrapRow({ settings, saveNotif, email, emailEnabled }) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const enabled = !!settings?.notifications?.eveningWrap;
  const canSend = !!email && emailEnabled;

  const sendNow = async () => {
    setSending(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/email/wrap/send-now`);
      setResult({ ok: true, msg: `Sent to ${r.data?.sent_to}` });
    } catch (e) {
      setResult({ ok: false, msg: e.response?.data?.detail || e.message || "Send failed" });
    }
    setSending(false);
  };

  return (
    <>
      <div className="srow">
        <div>
          <div className="slbl">Evening Wrap Email</div>
          <div className="ssub">Daily 4:00 PM ET: closing P/L + trades executed + any trail stops that triggered + risk events. Requires Email Alerts ON.</div>
        </div>
        <input
          type="checkbox"
          data-testid="setting-evening-wrap"
          checked={enabled}
          disabled={!emailEnabled}
          onChange={e => saveNotif("eveningWrap", e.target.checked)}
        />
      </div>
      {enabled && (
        <div className="srow" style={{ borderTop: "none" }}>
          <div>
            <div className="slbl">Send wrap now</div>
            <div className="ssub">Preview tonight's wrap with current data.</div>
            {result && (
              <div style={{ fontSize: 10, color: result.ok ? "var(--green)" : "var(--red)", marginTop: 4 }}>
                {result.ok ? "✓ " : "✗ "}{result.msg}
              </div>
            )}
          </div>
          <button
            data-testid="send-wrap-now-btn"
            onClick={sendNow}
            disabled={sending || !canSend}
            className="btn btn-blue"
            style={{ fontSize: 10, opacity: (sending || !canSend) ? 0.55 : 1 }}
          >{sending ? "Sending..." : "Send Now"}</button>
        </div>
      )}
    </>
  );
}

function MorningBriefingRow({ settings, saveNotif, email, emailEnabled }) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const enabled = !!settings?.notifications?.morningBriefing;
  const canSend = !!email && emailEnabled;

  const sendNow = async () => {
    setSending(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/email/briefing/send-now`);
      setResult({ ok: true, msg: `Sent to ${r.data?.sent_to}` });
    } catch (e) {
      setResult({ ok: false, msg: e.response?.data?.detail || e.message || "Send failed" });
    }
    setSending(false);
  };

  return (
    <>
      <div className="srow">
        <div>
          <div className="slbl">Morning Briefing Email</div>
          <div className="ssub">Daily 9:30 AM ET: positions + P/L + top Zi scores + armed trail stops + risk alerts. Requires Email Alerts ON.</div>
        </div>
        <input
          type="checkbox"
          data-testid="setting-morning-briefing"
          checked={enabled}
          disabled={!emailEnabled}
          onChange={e => saveNotif("morningBriefing", e.target.checked)}
        />
      </div>
      {enabled && (
        <div className="srow" style={{ borderTop: "none" }}>
          <div>
            <div className="slbl">Send briefing now</div>
            <div className="ssub">Dry-run the briefing to verify formatting + recipient.</div>
            {result && (
              <div style={{ fontSize: 10, color: result.ok ? "var(--green)" : "var(--red)", marginTop: 4 }}>
                {result.ok ? "✓ " : "✗ "}{result.msg}
              </div>
            )}
          </div>
          <button
            data-testid="send-briefing-now-btn"
            onClick={sendNow}
            disabled={sending || !canSend}
            className="btn btn-blue"
            style={{ fontSize: 10, opacity: (sending || !canSend) ? 0.55 : 1 }}
          >{sending ? "Sending..." : "Send Now"}</button>
        </div>
      )}
    </>
  );
}

// ─── Enable Browser Notifications Button ─────────────────────────────────────
export function EnableBrowserNotifButton() {
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  if (perm === "unsupported") return null;
  const handleEnable = async () => {
    try {
      const p = await Notification.requestPermission();
      setPerm(p);
      if (p === "granted") {
        try { new Notification("Omega ZI AI", { body: "Browser notifications enabled. Risk alerts will appear here.", icon: "/icon-192.png" }); } catch {}
      }
    } catch {}
  };
  const label = perm === "granted" ? "✓ Browser alerts enabled" : perm === "denied" ? "Blocked — enable in browser settings" : "Enable Browser Push Alerts";
  const color = perm === "granted" ? "#4ade80" : perm === "denied" ? "#f87171" : "#60a5fa";
  return (
    <div className="srow" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
      <div>
        <div className="slbl">Browser Notifications</div>
        <div className="ssub">OS-level pop-ups for Risk Guard and Zi Score alerts</div>
      </div>
      <button
        data-testid="enable-browser-notif-btn"
        onClick={handleEnable}
        disabled={perm === "granted" || perm === "denied"}
        style={{
          padding: "7px 14px", borderRadius: 20, border: `1px solid ${color}40`,
          background: `${color}18`, color, fontSize: 11, fontWeight: 700,
          cursor: (perm === "granted" || perm === "denied") ? "default" : "pointer",
        }}
      >{label}</button>
    </div>
  );
}
